import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockPostos } from '../mockPostos';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Battery, MapPin, Zap, Search, ChevronDown, ChevronUp, ExternalLink, Calendar, Clock, Activity } from 'lucide-react';

const PostosList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocal, setFilterLocal] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterEstado, setFilterEstado] = useState('all');
  const [expandedPostos, setExpandedPostos] = useState(new Set());

  const locais = ['Pragal', 'Santa Apolónia', 'Évora', 'Freixo'];

  const filteredPostos = mockPostos.filter(posto => {
    const matchSearch = posto.nome.toLowerCase().includes(searchTerm.toLowerCase()) || 
                       posto.local.toLowerCase().includes(searchTerm.toLowerCase());
    const matchLocal = filterLocal === 'all' || posto.local === filterLocal;
    const matchTipo = filterTipo === 'all' || posto.tipo === filterTipo;
    const matchEstado = filterEstado === 'all' || posto.estado === filterEstado;
    
    return matchSearch && matchLocal && matchTipo && matchEstado;
  });

  const getEstadoBadge = (estado) => {
    const variants = {
      'Disponível': 'bg-green-100 text-green-800 border-green-200',
      'Em Uso': 'bg-blue-100 text-blue-800 border-blue-200',
      'Manutenção': 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return variants[estado] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTipoBadge = (tipo) => {
    return tipo === 'DC' 
      ? 'bg-purple-100 text-purple-800 border-purple-200' 
      : 'bg-cyan-100 text-cyan-800 border-cyan-200';
  };

  const toggleExpand = (postoId) => {
    setExpandedPostos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postoId)) {
        newSet.delete(postoId);
      } else {
        newSet.add(postoId);
      }
      return newSet;
    });
  };

  const isExpanded = (postoId) => expandedPostos.has(postoId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Postos de Carregamento</h1>
        <p className="text-gray-600 mt-1">Gestão e monitorização de todos os postos</p>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Pesquisar postos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterLocal} onValueChange={setFilterLocal}>
          <SelectTrigger>
            <SelectValue placeholder="Todos os locais" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os locais</SelectItem>
            {locais.map(local => (
              <SelectItem key={local} value={local}>{local}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger>
            <SelectValue placeholder="Todos os tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="AC">AC</SelectItem>
            <SelectItem value="DC">DC</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterEstado} onValueChange={setFilterEstado}>
          <SelectTrigger>
            <SelectValue placeholder="Todos os estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estados</SelectItem>
            <SelectItem value="Disponível">Disponível</SelectItem>
            <SelectItem value="Em Uso">Em Uso</SelectItem>
            <SelectItem value="Manutenção">Manutenção</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Contador de resultados */}
      <div className="text-sm text-gray-600">
        {filteredPostos.length} posto{filteredPostos.length !== 1 ? 's' : ''} encontrado{filteredPostos.length !== 1 ? 's' : ''}
      </div>

      {/* Lista de Postos (Accordion) */}
      <div className="space-y-3">
        {filteredPostos.map(posto => (
          <Card 
            key={posto.id}
            className={`transition-all duration-200 ${isExpanded(posto.id) ? 'shadow-md ring-1 ring-[#003D7A]/20' : 'hover:shadow-sm'}`}
          >
            {/* Header - Sempre visível */}
            <div 
              className="px-4 py-3 cursor-pointer"
              onClick={() => toggleExpand(posto.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Número do posto */}
                  <div className="flex items-center gap-2">
                    <Battery className="h-5 w-5 text-[#003D7A]" />
                    <span className="font-bold text-gray-900">{posto.nome}</span>
                  </div>

                  {/* Localização */}
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{posto.local}</span>
                  </div>

                  {/* Tipo AC/DC */}
                  <Badge className={getTipoBadge(posto.tipo)}>
                    {posto.tipo}
                  </Badge>

                  {/* Potência */}
                  <div className="flex items-center gap-1 text-sm">
                    <Zap className="h-4 w-4 text-yellow-600" />
                    <span className="font-medium text-gray-900">{posto.potencia} kW</span>
                  </div>

                  {/* Estado */}
                  <Badge className={getEstadoBadge(posto.estado)}>
                    {posto.estado}
                  </Badge>
                </div>

                {/* Botão expandir/colapsar */}
                <div className="flex items-center gap-2">
                  {isExpanded(posto.id) ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Detalhes - Visível apenas quando expandido */}
            {isExpanded(posto.id) && (
              <CardContent className="pt-0 pb-4 border-t border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                  {/* Estatísticas */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Activity className="h-4 w-4 text-[#003D7A]" />
                      Estatísticas
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Utilização Mensal:</span>
                        <span className="font-semibold">{posto.utilizacaoMensal}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Carregamentos:</span>
                        <span className="font-semibold">{posto.totalCarregamentos}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Energia Total:</span>
                        <span className="font-semibold">{posto.energiaTotal?.toLocaleString('pt-PT') || 'N/A'} kWh</span>
                      </div>
                    </div>
                    {/* Barra de utilização */}
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 mb-1">Taxa de utilização</div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-[#003D7A] h-2 rounded-full transition-all"
                          style={{ width: `${posto.utilizacaoMensal}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Informações Técnicas */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-[#003D7A]" />
                      Informações Técnicas
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Conector:</span>
                        <span className="font-semibold">{posto.conector || 'CCS2'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tensão:</span>
                        <span className="font-semibold">{posto.tensao || '400V'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Marca:</span>
                        <span className="font-semibold">{posto.marca || 'ABB'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Últimas Atividades */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#003D7A]" />
                      Última Atividade
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Último Uso:</span>
                        <span className="font-semibold">{posto.ultimoUso || 'Há 2 horas'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Última Manutenção:</span>
                        <span className="font-semibold">{posto.ultimaManutencao || 'Há 15 dias'}</span>
                      </div>
                    </div>
                    
                    {/* Botão para ver detalhes completos */}
                    <Button 
                      className="w-full mt-3 bg-[#003D7A] hover:bg-[#002d5c]"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/carregamento/postos/${posto.id}`);
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Ver Ficha Completa
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {filteredPostos.length === 0 && (
        <div className="text-center py-12">
          <Battery className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nenhum posto encontrado</p>
        </div>
      )}
    </div>
  );
};

export default PostosList;
